import {
  ChevronLeft,
  Coins,
  FileText,
  Loader2,
  SendHorizontal,
} from "lucide-react";
import { useMemo, useState } from "react";

import { TEMPLATE_SEND_COST } from "@mandoob/shared";

import { useGetTemplates } from "@/api/templates";
import { ResponsiveDialog } from "@/components/shared/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/language";
import { isNative } from "@/lib/capacitor";
import { useSession } from "@/session";
import { formatTemplateName } from "./lib";

import { WhatsAppText } from "./whatsapp-text";

// Adapted from the reference template-picker-dialog.tsx: our TemplateDto,
// useGetTemplates, no "create template" actions (drivers can't manage
// templates) and a visible credit cost line. AUTO_FILLED kept as a concept
// but empty — the backend fills nothing on the driver's behalf in v1.
const AUTO_FILLED = new Set<string>([]);

const LANGUAGE_FLAGS: Record<string, string> = {
  en: "🇬🇧",
  en_us: "🇺🇸",
  en_gb: "🇬🇧",
  ar: "🇸🇦",
  es: "🇪🇸",
  fr: "🇫🇷",
  de: "🇩🇪",
  tr: "🇹🇷",
  ur: "🇵🇰",
  hi: "🇮🇳",
  id: "🇮🇩",
};

export function languageFlag(code?: string | null): string {
  if (!code) return "🌐";
  const key = code.toLowerCase().replace("-", "_");
  return LANGUAGE_FLAGS[key] ?? LANGUAGE_FLAGS[key.split("_")[0]] ?? "🌐";
}

function parseTokens(body?: string | null): string[] {
  if (!body) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  const regex = /\{\{\s*([^{}]+?)\s*\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(body)) !== null) {
    const token = match[1].trim();
    if (token && !seen.has(token)) {
      seen.add(token);
      out.push(token);
    }
  }
  return out;
}

export function TemplatePickerDialog({
  open,
  onClose,
  onSubmit,
  sending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (templateId: string, variables: Record<string, string>) => void;
  sending?: boolean;
}) {
  const { t, dir } = useLanguage();
  const session = useSession();
  const { data, isLoading } = useGetTemplates(
    { MaxResultCount: 100 },
    { query: { enabled: open } },
  );
  const templates = (data?.items ?? []).filter(
    (tpl) => !tpl.hasMediaHeader && tpl.status === "Approved",
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});

  const selected = templates.find((tpl) => tpl.id === selectedId) ?? null;
  const fields = useMemo(
    () => (selected?.variables ?? []).filter((name) => !AUTO_FILLED.has(name)),
    [selected],
  );
  const bodyTokens = useMemo(() => parseTokens(selected?.bodyText), [selected]);

  const preview = useMemo(() => {
    let body = selected?.bodyText ?? "";
    for (const token of bodyTokens) {
      const value = values[token]?.trim();
      body = body.replaceAll(`{{${token}}}`, value || `{{${token}}}`);
    }
    return body;
  }, [selected, bodyTokens, values]);

  const canSend =
    !!selected && fields.every((name) => (values[name]?.trim() ?? "") !== "");

  const reset = () => {
    setSelectedId(null);
    setValues({});
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleSelect = (id: string) => {
    setSelectedId(id);
    setValues({});
  };

  const handleSend = () => {
    if (!selected || !canSend) return;
    const variables: Record<string, string> = {};
    for (const name of fields) variables[name] = values[name].trim();
    onSubmit(selected.id, variables);
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={close}
      title={
        selected ? (
          <span className="tw:block tw:truncate tw:px-8">
            {formatTemplateName(selected.name)}
          </span>
        ) : (
          t("Mandoob:Template.PickerTitle")
        )
      }
      contentClassName="tw:rounded-xl"
      footer={
        selected ? (
          <div dir={dir} className="tw:flex tw:w-full tw:items-center tw:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setSelectedId(null)}
              disabled={sending}
              className="tw:rounded-full tw:border tw:bg-muted tw:shadow-none"
            >
              <ChevronLeft className="tw:size-4 tw:rtl:-scale-x-100" />
              {t("Mandoob:Back")}
            </Button>
            {!(session?.appPaymentsDisabled && isNative()) && (
              <span className="tw:ms-auto tw:inline-flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-muted-foreground">
                <Coins className="tw:size-4" strokeWidth={1.75} />
                {t("Mandoob:Template.Cost", TEMPLATE_SEND_COST)}
              </span>
            )}
            <Button
              type="button"
              size="icon"
              onClick={handleSend}
              disabled={!canSend || sending}
              aria-label={t("Mandoob:Template.Send")}
              className="tw:rounded-full"
            >
              {sending ? (
                <Loader2 className="tw:size-5 tw:animate-spin" />
              ) : (
                <SendHorizontal
                  className="tw:size-5 tw:rtl:-scale-x-100"
                  strokeWidth={1.75}
                />
              )}
            </Button>
          </div>
        ) : undefined
      }
    >
      <div dir={dir} className="tw:flex tw:flex-col tw:gap-3">
        {isLoading ? (
          <div className="tw:flex tw:items-center tw:justify-center tw:py-10">
            <Loader2 className="tw:size-5 tw:animate-spin tw:text-muted-foreground" />
          </div>
        ) : selected ? (
          <>
            <div className="tw:rounded-xl tw:border tw:border-border tw:bg-muted tw:p-3">
              <div className="tw:mb-1 tw:text-xs tw:font-semibold tw:text-muted-foreground">
                {t("Mandoob:Template.Preview")}
              </div>
              <p className="tw:m-0 tw:whitespace-pre-wrap tw:text-sm tw:text-foreground">
                <WhatsAppText text={preview} />
              </p>
            </div>
            {fields.length > 0 ? (
              <div className="tw:flex tw:flex-col tw:gap-2.5">
                {fields.map((name) => (
                  <label key={name} className="tw:flex tw:flex-col tw:gap-1">
                    <span className="tw:text-xs tw:font-semibold tw:text-muted-foreground">
                      {t("Mandoob:Template.Variable")} {name}
                    </span>
                    <Input
                      value={values[name] ?? ""}
                      onChange={(event) =>
                        setValues((prev) => ({
                          ...prev,
                          [name]: event.target.value,
                        }))
                      }
                    />
                  </label>
                ))}
              </div>
            ) : null}
          </>
        ) : templates.length === 0 ? (
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-3.5 tw:py-10 tw:text-center">
            <span className="tw:flex tw:size-15 tw:items-center tw:justify-center tw:rounded-2xl tw:bg-brand-50 tw:text-muted-foreground">
              <FileText
                className="tw:size-10 tw:stroke-brand"
                strokeWidth={1.6}
              />
            </span>
            <div>
              <p className="tw:m-0 tw:text-sm tw:font-semibold">
                {t("Mandoob:Template.PickerEmpty")}
              </p>
              <p className="tw:m-0 tw:max-w-[260px] tw:text-xs tw:text-muted-foreground">
                {t("Mandoob:Template.PickerEmptyHint")}
              </p>
            </div>
          </div>
        ) : (
          <div className="tw:flex tw:flex-col tw:gap-2">
            {templates.map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                onClick={() => handleSelect(tpl.id)}
                className="tw:flex tw:flex-col tw:gap-1 tw:rounded-xl tw:border tw:border-border tw:bg-muted tw:p-3 tw:text-start tw:text-foreground tw:transition-colors tw:hover:border-foreground/20"
              >
                <span className="tw:flex tw:items-center tw:gap-2">
                  <FileText
                    className="tw:size-4 tw:shrink-0"
                    strokeWidth={1.75}
                  />
                  <span className="tw:truncate tw:text-sm tw:font-semibold">
                    {formatTemplateName(tpl.name)}
                  </span>
                </span>
                {tpl.bodyText ? (
                  <span className="tw:line-clamp-2 tw:text-xs tw:text-muted-foreground">
                    <WhatsAppText text={tpl.bodyText} />
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        )}
      </div>
    </ResponsiveDialog>
  );
}
